// PlanBadgeV2 — cụm "gói đang dùng" trên thanh trên cùng của giao diện v2.
//
// Vì sao đưa lên header: gói + số chỗ còn lại trước đây chỉ xem được ở /marketplace và
// /billing. Người quản trị không nhớ mình đang ở bậc nào, tới lúc mời người thứ N+1 mới biết
// hết chỗ. Đưa lên thanh trên cùng thì trạng thái đó luôn nằm trong tầm mắt.
//
// Dữ liệu: `/users/seat-status` — endpoint NHẸ (đọc license + đếm member). Dùng chung
// `queryKey: ['seat-status']` với BillingPage và InvitationsPage ⇒ mở các trang đó không phát
// sinh request thứ hai.
//
// ── KHÁC bản cnvwork-saas ở hai điểm, đều do MÔ HÌNH GÓI của Connect còn ở đời trước ──
//
// 1. BẬC GÓI là CHUỖI. Work trả `plan_tier_level` (0/1/2) lấy từ `meta.plans.tier_level`;
//    Connect chưa có cột đó (và `meta.plans` còn trống), BE chỉ trả `plan_tier` =
//    'none' | 'free' | 'pro' (xem services/licenseService.js). Dùng thẳng chuỗi đó làm
//    `data-tier`, CSS đã khai đúng ba khoá này.
//
// 2. KHÔNG có bảng tên gói (`lib/planCopy` của Work). Bảng đó chứa tên sản phẩm CNV WORK
//    ("On-Cloud All-in-one", "CNV Work — Mua thêm user"…) — chép sang đây là dán tên sản phẩm
//    của thương hiệu khác lên header Connect. Connect cũng chưa bán gói nào qua `meta.plans`,
//    nên chưa có tên nào thật để khai. Vì vậy lấy thẳng `plan_label` của BE, và chỉ dịch câu
//    dự phòng. Khi Connect có danh mục gói thật thì thêm bảng tên ở đây, KHÔNG chép của Work.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Briefcase, AlertTriangle } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { fmtCurrency } from '@cnv/ui/i18n';
import { planApi, billingApi } from '../../lib/inboxClient';
import { useAuthStore } from '../../lib/authStore';

export default function PlanBadgeV2() {
  const { t } = useLingui();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['seat-status'],
    queryFn: () => planApi.seatStatus(),
    // Gói đổi theo đợt kích hoạt, không đổi từng phút. 5 phút là đủ tươi mà không bắn
    // request mỗi lần đổi trang.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Số credit trong ví của workspace (01/10/2026, sếp Phú: "show số tiền credit ra ở trên header" —
  // giống cụm "Số Credit còn lại" trên CDP). Cùng cửa quyền với trang Gói & Thanh toán: người
  // không xem được trang đó nhận 403 ⇒ query lỗi ⇒ cột này không hiện. Máy chủ đệm 60 giây.
  // Chỉ GỌI khi người này xem được menu "Gói & Thanh toán" (cùng luật AppLayout.userHasPermission):
  // thanh này nằm trên mọi trang, gọi mù thì người không có quyền ăn một lượt 403 mỗi trang.
  const user = useAuthStore((s) => s.user);
  const canSeeWallet = user?.role === 'admin'
    || (user?.permissions || []).some((p) => p === '*' || p === 'marketplace:view');
  const credit = useQuery({
    queryKey: ['billing-credit-balance'],
    queryFn: () => billingApi.balance(),
    enabled: canSeeWallet,
    staleTime: 60 * 1000,
    retry: false,
  });
  const creditBalance = credit.data?.available;

  // Chưa có dữ liệu thì KHÔNG dựng khung rỗng — thanh trên cùng nhảy layout mỗi lần load còn
  // khó chịu hơn là hiện muộn nửa giây.
  if (!data) return null;

  const quota = Number(data.quota) || 0;
  const used = Number(data.active_count) || 0;
  const unlimited = quota >= 999999;
  // Hết chỗ (hoặc đã có người bị khoá vì hết chỗ) = thứ quản trị viên cần thấy NGAY.
  const warn = !!data.over || Number(data.locked_count) > 0 || (!unlimited && quota > 0 && used >= quota);

  const label = data.plan_label || t`Chưa kích hoạt`;

  return (
    <button
      type="button"
      className={`v2-plan${warn ? ' v2-plan--warn' : ''}`}
      data-tier={data.plan_tier || 'none'}
      onClick={() => navigate('/marketplace')}
      title={t`Xem kho ứng dụng`}
    >
      {/* TÊN GÓI chỉ hiện khi workspace CHƯA có gói. Từ 21/08 cụm đổi workspace bên TRÁI
          thanh đã dán viên nhãn tên gói ngay cạnh tên workspace ⇒ để nguyên ở đây là một
          thanh 38px ghi tên gói hai lần cách nhau một gang tay (sếp Phú bắt).
          Nhưng KHÔNG bỏ hẳn: khi chưa kích hoạt thì viên nhãn bên trái tự ẩn, câu "Chưa
          kích hoạt" chỉ còn chỗ này để nói — bỏ luôn là mất tín hiệu, không phải bớt lặp. */}
      {data.plan_label ? null : (
        <span className="v2-plan__name">
          <Briefcase size={14} aria-hidden="true" />
          {label}
        </span>
      )}

      <span className="v2-plan__stats">
        <span className="v2-plan__stat">
          <i><Trans context="host">Người dùng</Trans></i>
          <b>{unlimited ? `${used}/∞` : `${used}/${quota}`}</b>
        </span>
        <span className="v2-plan__stat">
          <i><Trans>Hạn gói</Trans></i>
          {/* Hạn của GÓI NỀN, KHÔNG phải đợt hết sớm nhất: workspace còn đợt KM/add-on ngắn hơn
              thì `next_expiry` là hạn của đợt đó — khách mua gói tới 12/2027 mà thanh này ghi
              10/2026 rồi tưởng mình vẫn đang dùng thử (sếp Phú bắt 27.08.2026). */}
          <b>{(() => {
            const exp = data.plan_expiry ?? data.next_expiry;
            return exp ? format(new Date(exp), 'dd/MM/yyyy') : t({ context: 'host', message: `Không có hạn` });
          })()}</b>
        </span>
        {creditBalance != null && (
          <span className="v2-plan__stat">
            <i><Trans>Credit còn lại</Trans></i>
            <b>{fmtCurrency(creditBalance, credit.data.currency, { d: 2 })}</b>
          </span>
        )}
        {warn && <AlertTriangle size={14} className="v2-plan__warn-icon" aria-hidden="true" />}
      </span>
    </button>
  );
}
